// frontend/src/theme/ThemeContext.tsx
// Provides ShieldThemeProvider (wraps MUI ThemeProvider) and useShieldTheme hook.
// Persists theme choice in localStorage.
// Injects every token as a CSS custom property on :root for use in plain CSS.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import {
  ALL_THEMES,
  DEFAULT_THEME,
  DesignTokens,
  ThemeId,
} from './tokens';

// ── Context shape ─────────────────────────────────────────────────────────────

interface ThemeContextValue {
  themeId: ThemeId;
  tokens: DesignTokens;
  setTheme: (id: ThemeId) => void;
  allThemeIds: ThemeId[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ── localStorage key ──────────────────────────────────────────────────────────

const STORAGE_THEME_KEY = 'shield_theme_id';

function safeReadTheme(): ThemeId {
  try {
    const stored = localStorage.getItem(STORAGE_THEME_KEY) as ThemeId | null;
    if (stored && stored in ALL_THEMES) return stored;
  } catch (_) { /* ignore */ }
  return DEFAULT_THEME;
}

// ── MUI theme builder ─────────────────────────────────────────────────────────

function buildMuiTheme(t: DesignTokens) {
  return createTheme({
    palette: {
      mode: t.mode,
      primary:    { main: t.primary,  contrastText: t.textOnPrimary },
      secondary:  { main: t.accent },
      success:    { main: t.success },
      warning:    { main: t.warning },
      error:      { main: t.danger },
      info:       { main: t.info },
      background: { default: t.bgCanvas, paper: t.bgSurface },
      text: {
        primary:   t.textPrimary,
        secondary: t.textSecondary,
        disabled:  t.textDisabled,
      },
      divider: t.borderDefault,
    },
    typography: {
      fontFamily: t.fontBody,
      h1: { fontFamily: t.fontDisplay },
      h2: { fontFamily: t.fontDisplay },
      h3: { fontFamily: t.fontDisplay },
      h4: { fontFamily: t.fontDisplay },
      h5: { fontFamily: t.fontDisplay },
      h6: { fontFamily: t.fontDisplay },
    },
    shape: {
      borderRadius: parseInt(t.radiusMd, 10),
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t.bgSurface,
            boxShadow:       t.shadowCard,
            borderRadius:    t.radiusLg,
            border:          `1px solid ${t.borderSubtle}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius:  t.radiusMd,
            fontFamily:    t.fontBody,
            textTransform: 'none',
            fontWeight:    600,
          },
          containedPrimary: {
            background: t.gradientButton,
            color: t.textOnPrimary,
            boxShadow:  t.shadowCard,
            '&:hover': {
              background: t.primaryHover,
              boxShadow:  t.shadowGlow,
            },
            '&.Mui-disabled': {
              background: 'none',
              backgroundColor: t.primaryMuted,
              color: t.textSecondary,
            },
          },
          containedWarning: {
            color: t.mode === 'dark' ? '#FFFFFF' : undefined,
            '&.Mui-disabled': {
              background: 'none',
              backgroundColor: t.warningMuted,
              color: t.textSecondary,
            },
          },
          containedSuccess: {
            color: t.mode === 'dark' ? '#FFFFFF' : undefined,
            '&.Mui-disabled': {
              background: 'none',
              backgroundColor: t.successMuted,
              color: t.textSecondary,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: t.bgSurface,
            borderRadius:    t.radiusLg,
            boxShadow:       t.shadowCard,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: t.sidebarBg,
            borderRight:     `1px solid ${t.sidebarBorder}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: t.topbarBg,
            borderBottom:    `1px solid ${t.topbarBorder}`,
            backgroundImage: 'none',
            boxShadow:       'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: t.borderSubtle,
            color:       t.textPrimary,
          },
          head: {
            color:      t.textSecondary,
            fontWeight: 600,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: t.radiusSm },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.bgElevated,
            color:           t.textPrimary,
            border:          `1px solid ${t.borderDefault}`,
            borderRadius:    t.radiusMd,
            fontSize:        '12px',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: t.radiusFull },
          bar:  { borderRadius: t.radiusFull },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: t.borderSubtle },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: t.radiusMd,
            '&.Mui-selected': {
              backgroundColor: t.sidebarActiveBg,
              color:           t.sidebarTextActive,
            },
            '&:hover': {
              backgroundColor: t.sidebarHoverBg,
            },
          },
        },
      },
    },
  });
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ShieldThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeId, setThemeIdState] = useState<ThemeId>(safeReadTheme);

  const tokens   = ALL_THEMES[themeId];
  const muiTheme = useMemo(() => buildMuiTheme(tokens), [tokens]);

  // ── CSS custom properties on :root ─────────────────────────────────────
  useEffect(() => {
    const root = document.documentElement;

    // Inject every string token as --shield-<camelKey>
    (Object.entries(tokens) as [string, string | number][]).forEach(([key, value]) => {
      if (typeof value === 'string') {
        root.style.setProperty(`--shield-${key}`, value);
      }
    });

    // Data attributes for CSS targeting
    root.setAttribute('data-theme', themeId);
    root.setAttribute('data-mode',  tokens.mode);
  }, [tokens, themeId]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeIdState(id);
    try { localStorage.setItem(STORAGE_THEME_KEY, id); } catch (_) { /* ignore */ }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    themeId,
    tokens,
    setTheme,
    allThemeIds: Object.keys(ALL_THEMES) as ThemeId[],
  }), [themeId, tokens, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useShieldTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useShieldTheme must be used inside <ShieldThemeProvider>');
  return ctx;
}
